import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, resolveMediaUrl } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import { reelsPath } from '../paths';
import {
  fetchReelEligibleGuests,
  reelGuestSubtitle,
} from '../utils/reelEligibleGuests';

const STATUS_LABEL = {
  capturing: 'Capturing',
  ready: 'Ready to render',
  rendering: 'Rendering',
  completed: 'Ready',
  approved: 'Shared',
  failed: 'Failed',
  expired: 'Expired',
};

function sessionMeta(session) {
  const parts = [];
  const stage = session.stage || session.status || '';
  if (session.status === 'capturing' || session.status === 'ready') {
    if (session.clip_count != null && session.clip_count > 0) {
      parts.push(`${session.clip_count} clip${session.clip_count === 1 ? '' : 's'}`);
    }
    if (session.progress != null && session.progress > 0) {
      parts.push(`${session.progress}%`);
    }
  } else if (session.status === 'rendering') {
    parts.push(`${session.progress ?? 0}%`);
    if (stage) parts.push(stage);
  } else if (session.status === 'completed' || session.status === 'approved') {
    parts.push('Reel ready');
  } else if (session.status === 'failed') {
    parts.push(stage || 'Needs attention');
  } else if (stage) {
    parts.push(stage);
  }
  return parts.join(' · ') || '—';
}

function primaryActionLabel(session) {
  if (session.has_video) return 'Preview reel';
  if (session.status === 'rendering') return 'View status';
  if (session.status === 'failed') return 'Retry / status';
  if (session.status === 'ready') return 'Create reel';
  return 'Continue capture';
}

export default function VideoHome() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setGuestsLoading(true);
    setError('');
    try {
      const [data, eligible] = await Promise.all([
        api.listSessions(40),
        fetchReelEligibleGuests().catch(() => []),
      ]);
      setSessions(data.sessions || []);
      setGuests(eligible);
    } catch (e) {
      setError(e.message || 'Could not load sessions');
    } finally {
      setLoading(false);
      setGuestsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) return;
    if (!sessions.some((s) => s.id === selectedId)) setSelectedId('');
  }, [sessions, selectedId]);

  async function removeSession() {
    const id = deleteTarget?.id;
    if (!id) return;
    setBusyId(id);
    try {
      await api.deleteSession(id);
      if (selectedId === id) setSelectedId('');
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setBusyId('');
    }
  }

  function openSession(session) {
    const status = session.status;
    if (status === 'completed' || status === 'approved') {
      navigate(reelsPath('preview', session.id));
      return;
    }
    if (status === 'rendering' || status === 'ready' || status === 'failed') {
      navigate(reelsPath('rendering', session.id));
      return;
    }
    navigate(reelsPath('capture', session.id));
  }

  async function downloadSession(session) {
    const url = resolveMediaUrl(session.download_url || session.video_url);
    if (!url) return;
    const withDl = url.includes('download=') ? url : `${url}${url.includes('?') ? '&' : '?'}download=1`;
    try {
      const res = await fetch(withDl);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `naturals-reel-${session.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  }

  function toggleSelect(id) {
    setSelectedId((prev) => (prev === id ? '' : id));
  }

  return (
    <main className="video-home">
      <p className="eyebrow">Naturals Salon</p>
      <h1 className="brand video-home-title">Transformation videos</h1>
      <p className="lede video-home-lede">
        Pick a guest with a started service, then record. Finished reels download from Salon hub.
      </p>

      <div className="video-home-cta-stack">
        <button
          type="button"
          className="btn btn-primary video-home-cta"
          onClick={() => navigate(reelsPath('new'))}
        >
          <span>New capture</span>
          <span className="cta-chip" aria-hidden="true">
            Start
          </span>
        </button>
        <button
          type="button"
          className="btn btn-secondary video-home-cta"
          onClick={() => navigate(reelsPath('compress'))}
        >
          <span>Compress video</span>
          <span className="cta-chip" aria-hidden="true">
            65%
          </span>
        </button>
        <button type="button" className="service-select-link video-home-refresh" onClick={refresh} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh list'}
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <p className="service-select-section-label video-home-list-label">Guests in service</p>
      {guestsLoading ? <p className="lede">Loading guests…</p> : null}
      {!guestsLoading && !guests.length ? (
        <p className="lede video-home-guest-empty">
          No started services yet. Start service on Salon hub — guests appear here for reels.
        </p>
      ) : null}
      <div className="reel-guest-list" role="list">
        {guests.map((g) => (
          <button
            key={g.id}
            type="button"
            role="listitem"
            className="reel-guest-row"
            onClick={() =>
              navigate(reelsPath('new'), { state: { checkinId: g.id, checkin: g, guestName: g.customer_name } })
            }
          >
            <span className="reel-guest-row__main">
              <strong>{g.customer_name || 'Guest'}</strong>
              {g.customer_code ? <span className="reel-guest-row__code">{g.customer_code}</span> : null}
            </span>
            <span className="reel-guest-row__sub">{reelGuestSubtitle(g)}</span>
            {g.mobile ? <span className="reel-guest-row__mobile">{g.mobile}</span> : null}
          </button>
        ))}
      </div>

      {loading && !sessions.length ? <p className="lede">Loading sessions…</p> : null}

      {sessions.length ? (
        <p className="service-select-section-label video-home-list-label">Your sessions</p>
      ) : null}

      <div className="session-list" role="list">
        {sessions.map((session) => {
          const open = selectedId === session.id;
          return (
            <article
              key={session.id}
              className={`session-card status-${session.status}${open ? ' is-open' : ''}`}
              role="listitem"
            >
              <button
                type="button"
                className="session-card-header"
                aria-expanded={open}
                onClick={() => toggleSelect(session.id)}
              >
                <div className="session-card-top">
                  <h3>
                    {(session.service_type || 'service').replace('_', ' ')}
                    {session.customer_code ? ` · ${session.customer_code}` : ''}
                  </h3>
                  <span className="session-status">{STATUS_LABEL[session.status] || session.status}</span>
                </div>
                <p className="session-meta">{sessionMeta(session)}</p>
                {session.error_message && open ? (
                  <p className="session-error">{session.error_message.split('(')[0].trim()}</p>
                ) : null}
                <span className="session-card-chevron" aria-hidden="true">
                  {open ? '▾' : '▸'}
                </span>
              </button>

              {open ? (
                <div className="session-actions">
                  <button
                    type="button"
                    className="btn btn-primary session-action-primary"
                    onClick={() => openSession(session)}
                  >
                    {primaryActionLabel(session)}
                  </button>
                  {session.has_video ? (
                    <button type="button" className="btn btn-secondary" onClick={() => downloadSession(session)}>
                      Download
                    </button>
                  ) : null}
                  {session.can_delete ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busyId === session.id}
                      onClick={() =>
                        setDeleteTarget({
                          id: session.id,
                          label: (session.service_type || 'capture').replace('_', ' '),
                        })
                      }
                    >
                      Delete from storage
                    </button>
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
        {!loading && !sessions.length ? (
          <div className="video-home-empty">
            <p className="lede">No sessions yet.</p>
            <p className="video-home-empty-hint">Tap New capture to film Before → During → After.</p>
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        danger
        busy={Boolean(busyId)}
        title="Delete this reel everywhere?"
        body={
          deleteTarget
            ? `“${deleteTarget.label}” will be removed from the database and all cloud storage (clips, rendered video, thumbnails). This cannot be undone.`
            : ''
        }
        confirmLabel="Delete from storage"
        cancelLabel="Keep it"
        onConfirm={removeSession}
        onCancel={() => {
          if (!busyId) setDeleteTarget(null);
        }}
      />
    </main>
  );
}
